/**
 *  @module DeviceChecker
 *  @desc Provides Chromecast device checks and attempts best guess at device in use
 */
import config from 'config';
import { senderDebugger } from 'lib/debug/sender-receiver-debug';

class DeviceChecker {
  constructor() {
    window.deviceDetect = this; // to be removed after testing - allows easier debugging
    this.highSupportChecks = config.bitrateUnCappedDevices;
    this.hdcpVersion = '';

    this.supportedResolutions = {
      '720p': { width: 1280, height: 720 },
      '1080p': { width: 1920, height: 1080 },
      '2160p': { width: 3840, height: 2160 }
    };

    this.gen4BuildIds = [
      'stte.230319.008.r1',
      'sttl.241013.003',
      'stte.240615.007'
    ];
    this.gen4UltraBuildIds = [
      'stte.230319.008.h1',
      'sttl.241013'];

    this.codecs = {
      h264Lvl4Dot1: 'avc1.640028,mp4a.40.5',
      h264Lvl4Dot2: 'avc1.64002A,mp4a.40.5', // no 2ndGen
      h264Lvl4Dot3: 'avc1.64001f,mp4a.40.5',
      h264Lvl4Dot4: 'avc1.42E01E, mp4a.40.2',
      h265: 'hev1.1.6.L150.B0',
      h265_93: 'hev1.1.6.L93.00',
      h265_123_B0: 'hev1.1.6.L123.B0',
      h265_123_00: 'hev1.1.6.L123.00',
      h265_153_00: 'hev1.1.6.L153.00',
      av01_215M: 'av01.2.15M.10.0.100.09.16.09.0',
      av02_pllt: 'av01.P.LLT.DD.M.CCC.cp.tc.mc.F',
      avc3_64001f: 'avc3.64001f,mp4a.40.5',
      avc1_640029: 'avc1.640029,mp4a.40.5',
      avc1_4d401f: 'avc1.4d401f,mp4a.40.5',
      avc1_42d01e: 'avc1.42d01e,mp4a.40.5',
      avc1_42c01e: 'avc1.42c01e,mp4a.40.5',
      avc1_42c015: 'avc3.42c015,mp4a.40.5',
      vp08_00: 'vp8, vorbis',
      vp09_00: 'vp09.00.10.08',
      av1_0_08M: 'av01.0.08M.08',
      av1_0_0_10B: 'av01.0.00M.10'
    };
    this.context = window.cast && window.cast.framework && window.cast.framework.CastReceiverContext.getInstance();
    this.devices = {
      'googleTvStreamer': {
        mimeType: 'video/webm',
        codec: this.codecs.vp09_00,
        resolution: this.supportedResolutions['1080p'],
        framerate: 60
      },
      'ultra4thGen': {
        mimeType: 'video/mp4',
        codec: this.codecs.vp09_00,
        resolution: this.supportedResolutions['1080p'],
        framerate: 60
      },
      '4thGen': {
        mimeType: 'video/mp4',
        codec: this.codecs.vp09_00,
        resolution: this.supportedResolutions['1080p'],
        framerate: 30
      },
      'ultra3rdGen': {
        mimeType: 'video/mp4',
        codec: this.codecs.h265,
        resolution: this.supportedResolutions['1080p'],
        framerate: 30
      },
      '3rdGen': {
        mimeType: 'video/mp4',
        codec: this.codecs.h264Lvl4Dot2,
        resolution: this.supportedResolutions['1080p'],
        framerate: 30
      },
      '2ndGen': {
        mimeType: 'video/mp4',
        codec: this.codecs.h264Lvl4Dot4,
        resolution: this.supportedResolutions['720p'],
        framerate: 30
      },
      '1stGen': {
        mimeType: 'video/mp4',
        codec: this.codecs.h264Lvl4Dot1,
        resolution: this.supportedResolutions['720p'],
        framerate: 30
      },
      'builtIn': {
        mimeType: 'video/mp4',
        codec: this.codecs.h265,
        resolution: this.supportedResolutions['2160p'],
        framerate: 30
      },
      'hubMax': {
        mimeType: 'video/mp4',
        codec: this.codecs.h264Lvl4Dot2,
        resolution: this.supportedResolutions['1080p'],
        framerate: 30
      },
      'hub': {
        mimeType: 'video/mp4',
        codec: this.codecs.h264Lvl4Dot1,
        resolution: this.supportedResolutions['720p'],
        framerate: 30
      }
    };
    this.deviceList = [
      { name: 'googleTvStreamer', checkMethod: 'canDeviceSupportGoogleTvStreamer' },
      { name: 'ultra4thGen', checkMethod: 'canDeviceSupportUltra4thGen' },
      { name: '4thGen', checkMethod: 'canDeviceSupport4thGen' },
      { name: 'ultra3rdGen', checkMethod: 'canDeviceSupportUltra3rdGen' },
      { name: '3rdGen', checkMethod: 'canDeviceSupport3rdGen' },
      { name: '2ndGen', checkMethod: 'canDeviceSupport2ndGen' },
      { name: '1stGen', checkMethod: 'canDeviceSupport1stGen' },
      { name: 'hubMax', checkMethod: 'canDeviceSupportNestHubMax' },
      { name: 'hub', checkMethod: 'canDeviceSupportNestHub' },
      { name: 'builtIn', checkMethod: 'canDeviceSupportBuiltIn' }
    ];
    this.allDeviceChecks = this.allDeviceChecks.bind(this);
  }

  async allDeviceChecks() {
    if (!this.context) {
      return 'No Chromecast Context Found';
    }
    const deviceResults = [];
    if (this.highSupportChecks === undefined) {
      this.highSupportChecks = config.bitrateUnCappedDevices;
    }
    const deviceCapabilities = JSON.stringify(this.context.getDeviceCapabilities(), undefined, 4);
    const deviceOS = this.getDevicesOSVersion();
    const currentUserAgent = navigator.userAgent.toString();
    this.deviceModelBestGuess = this.deviceModelBestGuess ? this.deviceModelBestGuess : this.getModelDeviceBestGuess();
    this.hdcpVersion = await this.getHdcpVersion();

    deviceResults.push(`Chromecast User Agent: ${currentUserAgent}`);
    deviceResults.push(`Chromecast Device Capabilities: ${deviceCapabilities}`);
    deviceResults.push(`Chromecast DeviceData HDCP CALL: ${this.hdcpVersion}`);
    deviceResults.push(`DeviceOS: ${deviceOS}`);
    deviceResults.push(`Chromecast Device Best Guess: ${this.deviceModelBestGuess}`);
    deviceResults.push(`Chromecast High Support Devices: ${JSON.stringify(this.highSupportChecks)}`);

    for (const deviceObj of this.deviceList) {
      const methodCheck = await this[deviceObj.checkMethod]();

      deviceResults.push({
        deviceTypeCheck: `${deviceObj.name} - TEST Result: ${methodCheck}`
      });
    }
    const deviceData = Object.assign({}, deviceResults);

    senderDebugger.debugPlayerMessage('[deviceDetect][allDeviceChecks] RESULTS:', {
      deviceCheckData: deviceData
    });
    return deviceData;
  }
  getCodecResults() {
    const deviceDetails = {
      message: '[vp9tests]: ',
      detail: {
        displayResult: this.displayTestSuite(),
        VP9_tests: this.mediaCodecTests()
      }
    };
    return deviceDetails;
  }
  displayTestSuite(displayResult = []) {
    for (const [codecKey, codecValue] of Object.entries(this.codecs)) {
      const result = this.displayTest(codecValue);
      displayResult.push({ codec: codecKey, result });
    }
    return displayResult;
  }
  async getDeviceDetails() {
    const deviceCapabilities = this.context.getDeviceCapabilities();
    const displayResults = [];
    const supportChecks = {
      'googleTvStreamer': await this.canDeviceSupport('googleTvStreamer'),
      'ultra4thGen': await this.canDeviceSupport('ultra4thGen'),
      '4thGen': await this.canDeviceSupport('4thGen'),
      'ultra3rdGen': await this.canDeviceSupport('ultra3rdGen'),
      '3rdGen': await this.canDeviceSupport('3rdGen'),
      '2ndGen': await this.canDeviceSupport('2ndGen'),
      '1stGen': await this.canDeviceSupport('1stGen'),
      'builtIn': await this.canDeviceSupport('builtIn'),
      'hub': await this.canDeviceSupport('hub'),
      'hubMax': await this.canDeviceSupport('hubMax')
    };
    this.displayTestSuite(displayResults);
    const mediaCodecTests = this.mediaCodecTests();
    const deviceDetails = {
      message: '[deviceDetails]: ',
      detail: {
        bestGuessDevice: this.deviceModelBestGuess,
        osVersion: this.getDevicesOSVersion(),
        userAgent: navigator.userAgent.toString(),
        HDRSupport: this.canDeviceSupportHDR(),
        hdcpVersion: this.hdcpVersion,
        capabilities: deviceCapabilities,
        VP9_tests: mediaCodecTests,
        deviceSupportList: supportChecks,
        displayResult: displayResults
      }
    };

    return deviceDetails;
  }
  mediaCodecTests() {
    const codecTests = [
      {
        codec: 'avc1.42E01E, mp4a.40.2',
        description: 'H264 mp4 profile - basic video/mp4; codecs=avc1.42E01E, mp4a.40.2'
      },
      {
        codec: 'avc1.640028,mp4a.40.5',
        description: 'H264 mp4 profile - basic video/mp4; codecs=avc1.640028,mp4a.40.5'
      },
      {
        codec: 'avc1.64002A,mp4a.40.5',
        description: 'H264 mp4 profile - basic video/mp4; codecs=avc1.64002A,mp4a.40.5'
      },
      {
        codec: 'avc1.64001f,mp4a.40.5',
        description: 'H264 mp4 profile - basic video/mp4; codecs=avc1.64001f,mp4a.40.5'
      },
      {
        codec: 'vp8, vorbis',
        description: 'Basic VP8 Support: VP8, codec: vp8 vorbis',
        mimeType: 'video/webm'
      },
      {
        codec: 'vp09.00.10.08.02.08',
        description: 'VP9 Profile 0, codec: vp09.00.10.08.02.08 - Profile 0, level 1, bit depth 8, 4:2:2 chroma subsampling',
        mimeType: 'video/webm'
      },
      {
        codec: 'vp09.00.20.08.02.08',
        description: 'VP9 Profile 0, codec: vp09.00.20.08.02.08 - Profile 0, level 2, bit depth 8, 4:2:2 chroma subsampling',
        mimeType: 'video/webm'
      },
      {
        codec: 'vp09.00.30.08.02.08',
        description: 'VP9 Profile 0, codec: vp09.00.30.08.02.08 - Profile 0, level 3, bit depth 8, 4:2:2 chroma subsampling',
        mimeType: 'video/webm'
      },
      {
        codec: 'vp09.00.40.08.02.08',
        description: 'VP9 Profile 0, codec: vp09.00.40.08.02.08 - Profile 0, level 4, bit depth 8, 4:2:2 chroma subsampling',
        mimeType: 'video/webm'
      },
      {
        codec: 'vp09.00.50.08.02.08',
        description: 'VP9 Profile 0, codec: vp09.00.50.08.02.08 - Profile 0, level 5, bit depth 8, 4:2:2 chroma subsampling',
        mimeType: 'video/webm'
      },
      {
        codec: 'vp09.01.10.08.02.08',
        description: 'VP9 Profile 1, codec: vp09.01.10.08.02.08 - Profile 1, level 1, bit depth 8, 4:2:2 chroma subsampling',
        mimeType: 'video/webm'
      },
      {
        codec: 'vp09.02.10.08.02.08',
        description: 'VP9 Profile 2, codec: vp09.02.10.08.02.08 - Profile 2, level 1, bit depth 8, 4:2:2 chroma subsampling',
        mimeType: 'video/webm'
      },
      {
        codec: 'vp09.01.20.08.01',
        description: 'VP9 Profile 1, codec: vp09.01.20.08.01 - Profile 1, level 2, bit depth 8, 4:2:0 chroma subsampling',
        mimeType: 'video/webm'
      },
      {
        codec: 'vp09.01.20.08.01.01.01.01.00',
        description: 'VP9 Profile 1, codec: vp09.01.20.08.01.01.01.01.00 - ' +
        'Profile 1, level 2, bit depth 8, 4:2:0 chroma subsampling, REC709 color/transfer/matrix',
        mimeType: 'video/webm'
      },
      {
        codec: 'vp09.02.10.10.01.09.16.09.01',
        description: 'VP9 Profile 2, codec: vp09.02.10.10.01.09.16.09.01 - ' +
        'Profile 2, level 1, 10-bit YUV content, 4:2:0 chroma subsampling, ITU-R BT.2020 primaries',
        mimeType: 'video/webm'
      },
      {
        codec: 'av01.0.00M.10',
        description: 'AV1 Profile 0 - av1 codec video/mp4; codecs=av01.0.00M.10',
        mimeType: 'video/av01'
      },
      {
        codec: 'av01.0.05M',
        description: 'AV1 Profile 0 - av1 codec video/mp4; codecs=av01.0.05M',
        mimeType: 'video/av01'
      },
      {
        codec: 'av01.0.08M',
        description: 'AV1 Profile 0 - av1 codec video/mp4; codecs=av01.0.08M',
        mimeType: 'video/av01'
      },
      {
        codec: 'av01.0.15M',
        description: 'AV1 Profile 0 - av1 codec video/mp4; codecs=av01.0.15M.10',
        mimeType: 'video/av01'
      },
      {
        codec: 'av01.01.08M.10',
        description: 'AV1 Profile 1 - av1 codec video/mp4; codecs=av01.01.08M.10',
        mimeType: 'video/av01'
      },
      {
        codec: 'hvc1.1.4.L51.80',
        description: 'H.265 - hvc1 codec main10 - ' +
        'MP4 container with HEVC video encoded using the hvc1 codec (H.265), Main 10 Profile, Level 5.1'
      },
      {
        codec: 'hvc1.1.4.L51.80, mp4a.40.2',
        description: 'H.265 - hvc1 codec main10 with audio -' +
        'MP4 container with HEVC video encoded using the hvc1 codec (H.265), Main 10 Profile, Level 5.1, ' +
        'and AAC audio encoded using the mp4a.40.2 codec'
      },
      {
        codec: 'avc1.42c01e',
        description: 'H.264 - avc1 codec - MP4 container with H.264 video encoded using the avc1 codec'
      },
      {
        codec: 'avc1.42c01e, mp4a.40.2',
        description: 'H.264 - avc1 codec with audio -' +
        'MP4 container with H.264 video encoded using the avc1 codec and AAC audio encoded using the mp4a.40.2 codec'
      }
    ];

    const mediaCodecTests = codecTests.map(({ codec, description, mimeType = 'video/mp4' }) => {
      const isSupported = MediaSource.isTypeSupported(`${mimeType}; codecs="${codec}"`);
      return isSupported
        ? `> PASSED Supports: ${description}`
        : `> FAILED ${description} Profile Failed.`;
    });

    return mediaCodecTests;
  }

  getModelDeviceBestGuess() {
    return (this.deviceList.find((deviceObj) => {
      return this[deviceObj.checkMethod]();
    }) || {}).name || 'unknown device';
  }

  getCurrentDeviceSupport() {
    if (!this.context) {
      return 'No Chromecast Context Found';
    }
    const deviceCapabilities = this.context.getDeviceCapabilities();
    return `
      Config Partner List: 
      ${JSON.stringify(config.bitrateUnCappedDevices, undefined, 4)}

      Chromecast Device Capabilities: 
      ${JSON.stringify(deviceCapabilities, undefined, 4)}
    `;
  }

  checkHighBitrateSupport() {
    if (this.canDeviceSupportHDR()) {
      return true;
    }
    if (this.highSupportChecks === undefined) {
      this.highSupportChecks = config.bitrateUnCappedDevices;
    }
    const currentDevice = this.deviceModelBestGuess;
    const inHighSupportList = this.highSupportChecks && this.highSupportChecks.includes(currentDevice);
    const deviceObj = this.deviceList.find((deviceObj) => deviceObj.name === currentDevice);

    if (inHighSupportList) {
      return this[deviceObj.checkMethod]();
    }
    return false;
  }

  canDeviceSupportHDR() {
    if (!this.context) {
      return 'No Chromecast Context Found';
    }
    const deviceCapabilities = this.context && this.context.getDeviceCapabilities() || {};
    const { is_hdr_supported: isHdrSupported } = deviceCapabilities;
    return isHdrSupported;
  }
  async getHdcpVersion() {
    if (this.deviceModelBestGuess === '2ndGen') {
      return 1.4;
    }
    const castPlatformDisplay = window.cast && window.cast.__platform__ && window.cast.__platform__.display;
    return castPlatformDisplay ? await castPlatformDisplay.getHdcpVersion() : (this.canDeviceSupportGoogleTvStreamer() ? 2.2 : 1.4);
  }

  async canDeviceSupport(deviceName) {
    if (!this.context) {
      return 'No Chromecast Context Found';
    }
    const { mimeType, codec, resolution, framerate } = this.devices[deviceName];
    const mediaSupported = MediaSource.isTypeSupported(`${mimeType}; codecs="${codec}"`);

    return mediaSupported && this.context.canDisplayType &&
      this.context.canDisplayType(mimeType, codec, resolution.width, resolution.height, framerate);
  }
  displayTest(codecString) {
    const displayResults = [];
    if (!this.context.canDisplayType) {
      return { result: false };
    }
    Object.entries(this.supportedResolutions).forEach(async (resolution) => {
      const displayResult = this.context.canDisplayType('video/mp4', codecString, resolution[1].width, resolution[1].height, 30);
      displayResults.push({ message: `${codecString} at ${resolution[0]} :
       30FPS = ${displayResult}`, support: displayResult,
      codec: codecString,
      width: resolution[1].width,
      height: resolution[1].height,
      frameRate: 30 });
    });
    Object.entries(this.supportedResolutions).forEach(async (resolution) => {
      const displayResult = this.context.canDisplayType('video/mp4', codecString, resolution[1].width, resolution[1].height, 60);
      displayResults.push({
        message: `${codecString} at ${resolution[0]} : 60FPS = ${displayResult}`,
        support: displayResult,
        codec: codecString,
        width: resolution[1].width,
        height: resolution[1].height,
        frameRate: 60 });
    });
    return displayResults;
  }

  getDeviceType(userAgent) {
    const deviceTypeMatch = userAgent.match(/DeviceType\/([a-zA-Z]+)/);
    const deviceType = deviceTypeMatch ? deviceTypeMatch[1] : null;
    return deviceType && deviceType.toLowerCase() === 'chromecast';
  }

  canDeviceSupportGoogleTvStreamer() {
    const userAgent = navigator.userAgent.toString();
    const androidOS = userAgent.toLowerCase().search('android') >= 0;
    if (!androidOS) {
      return false;
    }

    // Example User Agent:
    // Mozilla/5.0 (Linux; Android 14.0; Build/UTTK.241210.003)
    // AppleWebKit/537.36 (KHTML, like Gecko)
    // Chrome/92.0.4515.0 Safari/537.36
    // CrKey/1.56.500000 DeviceType/AndroidTV
    const androidVersionMatch = userAgent.match(/android\s([\d.]+)/i);
    const androidOSVersion = androidVersionMatch ? androidVersionMatch[1] : null;
    const androidDigit = androidOSVersion ? parseInt(androidOSVersion.split('.')[0], 10) : null;

    const buildOf5thGenUHD = userAgent.toLowerCase().search('uttk.241210') >= 0;
    // check for gen4 directly
    const gen4BuildIds = ['stte.230319', 'sttl.241013', 'stte.230319', 'sttl.241013'];
    const buildOf4thGen = gen4BuildIds.map((buildId) => {
      return userAgent.toLowerCase().search(buildId) >= 0;
    });
    const is4thGen = buildOf4thGen.reduce((result, currentValue) => {
      return result || currentValue;
    }, false);

    if (is4thGen || (androidDigit && androidDigit <= 12)) {
      return false;
    }

    return buildOf5thGenUHD || (androidDigit && androidDigit >= 14) && androidOS;
  }
  canDeviceSupportUltra4thGen() {
    const userAgent = navigator.userAgent.toString();
    const androidOS = userAgent.toLowerCase().search('android') >= 0;
    const buildOf4thGenUHD = this.gen4UltraBuildIds.map((buildId) => {
      return userAgent.toLowerCase().search(buildId) >= 0;
    });
    const is4thGen = buildOf4thGenUHD.reduce((result, currentValue) => {
      return result || currentValue;
    }, false);
    return is4thGen && androidOS;
  }

  canDeviceSupport4thGen() {
    const userAgent = navigator.userAgent.toString();
    const androidOS = userAgent.toLowerCase().search('android') >= 0;
    const buildOf4thGen = this.gen4BuildIds.map((buildId) => {
      return userAgent.toLowerCase().search(buildId) >= 0;
    });
    const is4thGen = buildOf4thGen.reduce((result, currentValue) => {
      return result || currentValue;
    }, false);

    return is4thGen && androidOS;
  }

  canDeviceSupportUltra3rdGen() {
    const userAgent = navigator.userAgent.toString();
    const cpuPlatform = userAgent.toLowerCase().search('aarch64') >= 0;
    const isDeviceChromecastOS = this.getDeviceType(userAgent);

    return isDeviceChromecastOS && cpuPlatform;
  }

  /*
   *  Example gen3 User Agent:
   * "userAgent": "Mozilla/5.0 (X11; Linux armv7l)
   * AppleWebKit/537.36 (KHTML, like Gecko)
   * Chrome/90.0.4430.225
   * Safari/537.36
   * CrKey/1.56.500000
   * DeviceType/Chromecast",
  **/
  canDeviceSupport3rdGen() {
    const userAgent = navigator.userAgent.toString();
    const cpuPlatform = userAgent.toLowerCase().search('armv7l') >= 0;
    const isDeviceChromecastOS = this.getDeviceType(userAgent);
    // Prevents built-in tv devices with HDR support from being ID as gen3
    if (this.canDeviceSupportHDR()) {
      return false;
    }

    return isDeviceChromecastOS && cpuPlatform && this.canDeviceSupport('3rdGen');
  }

  canDeviceSupport2ndGen() {
    if (!this.context) {
      return 'No Chromecast Context Found';
    }
    if (this.canDeviceSupportHDR()) {
      return false;
    }
    const userAgent = navigator.userAgent.toString();
    const cpuPlatform = userAgent.toLowerCase().search('x11; linux armv7l') >= 0;
    // get the chrome version out of the user agent
    const chromeVersion = this.getChromeVersion();
    const chromeVersionNumber = parseInt(chromeVersion.split('.')[0], 10);
    const chromeVersionPassed = chromeVersionNumber >= 90;
    const deviceOs = this.getDevicesOSVersion();
    const osVersionPassed = this.isOSHigherThanOrEqual(deviceOs, '1.56.500000');
    const { mimeType, codec } = this.devices['2ndGen'];

    if (!chromeVersionPassed || !osVersionPassed ) {
      return false;
    }
    const mediaSupported = MediaSource.isTypeSupported(`${mimeType}; codecs="${codec}"`);

    return cpuPlatform && mediaSupported && osVersionPassed;
  }

  canDeviceSupport1stGen() {
    const userAgent = navigator.userAgent.toString();
    const cpuPlatform = userAgent.toLowerCase().search('x11; linux armv7l') >= 0;

    return cpuPlatform && this.canDeviceSupport('1stGen') && this.isOSLowerThanOrEqual(this.getDevicesOSVersion(), '1.36.159268');
  }

  canDeviceSupportNestHubMax() {
    if (!this.context) {
      return 'No Chromecast Context Found';
    }
    const userAgent = navigator.userAgent.toString();
    const cpuPlatform = userAgent.toLowerCase().search('fuchsia') >= 0;

    const { mimeType, codec, resolution, framerate } = this.devices.hubMax;
    const touchSupport = this.context.getDeviceCapabilities()?.touch_input_supported || false;
    return cpuPlatform && touchSupport && this.context.canDisplayType(mimeType, codec, resolution.width, resolution.height, framerate);
  }
  canDeviceSupportNestHub() {
    if (!this.context) {
      return 'No Chromecast Context Found';
    }
    const userAgent = navigator.userAgent.toString();
    const cpuPlatform = userAgent.toLowerCase().search('fuchsia') >= 0;

    const { mimeType, codec, resolution, framerate } = this.devices.hub;
    const touchSupport = this.context.getDeviceCapabilities()?.touch_input_supported || false;
    return cpuPlatform && touchSupport && this.context.canDisplayType(mimeType, codec, resolution.width, resolution.height, framerate);
  }
  canDeviceSupportBuiltIn() {
    return this.canDeviceSupport('builtIn');
  }

  getDevicesOSVersion() {
    // attempts to pull the CRKey value from the user agent
    const userAgent = navigator.userAgent.toString();
    const crKeyMatch = userAgent.match(/CrKey\/([\d.]+)/);
    if (!crKeyMatch) {
      console.warn('[DeviceChecker][getDevicesOSVersion] CrKey value not found in user agent:', userAgent);
    }
    return crKeyMatch ? crKeyMatch[1] : 'unknown';
  }

  getChromeVersion() {
    // attempts to pull the Chrome version from the user agent
    const userAgent = navigator.userAgent.toString();
    const chromeVersionMatch = userAgent.match(/Chrome\/([\d.]+)/);
    if (!chromeVersionMatch) {
      console.warn('Chrome version not found in user agent:', userAgent);
    }
    return chromeVersionMatch ? chromeVersionMatch[1] : 'unknown';
  }

  isOSLowerThanOrEqual(osVersion, expectedOS) {
    if (!osVersion || !expectedOS) {
      console.error('Invalid OS version or expected OS:', osVersion, expectedOS);
      return false;
    }

    const os = osVersion.split('.', 2).map(Number);
    const expectedOSArray = expectedOS.split('.', 2).map(Number);

    if (os.length < 2 || expectedOSArray.length < 2 || os.some(isNaN) || expectedOSArray.some(isNaN)) {
      console.error('Invalid OS version format or non-numeric values:', osVersion, expectedOS);
      return false;
    }

    return os[0] < expectedOSArray[0] || (os[0] === expectedOSArray[0] && os[1] <= expectedOSArray[1]);
  }

  isOSHigherThanOrEqual(osVersion, expectedOS) {
    const os = osVersion.split('.', 3);
    const expectedOSArray = expectedOS.split('.', 3);
    if (Number(os[1]) === Number(expectedOSArray[1])) {
      return Number(os[2]) >= Number(expectedOSArray[2]);
    }
    return Number(os[1]) > Number(expectedOSArray[1]);
  }
}

const deviceChecker = new DeviceChecker();
export const allDeviceChecks = deviceChecker.allDeviceChecks.bind(deviceChecker);
export const checkHighBitrateSupport = deviceChecker.checkHighBitrateSupport.bind(deviceChecker);
export const getModelDeviceBestGuess = deviceChecker.getModelDeviceBestGuess.bind(deviceChecker);
export const getCurrentDeviceSupport = deviceChecker.getCurrentDeviceSupport.bind(deviceChecker);
export const getDeviceDetails = deviceChecker.getDeviceDetails.bind(deviceChecker);
export const getCodecResults = deviceChecker.getCodecResults.bind(deviceChecker);
export const getHdcpVersion = deviceChecker.getHdcpVersion.bind(deviceChecker);
